import React, { useCallback, useRef, useState } from "react";
import { ChevronsLeftRight } from "lucide-react";

import beforeImg from "@/assets/providedassets/before.jpeg";
import afterImg from "@/assets/providedassets/after.jpeg";

export default function BeforeAfter() {
  const [position, setPosition] = useState(50); // percentage
  const containerRef = useRef(null);
  const draggingRef = useRef(false);

  const updateFromClientX = useCallback((clientX) => {
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;
    const pct = ((clientX - rect.left) / rect.width) * 100;
    setPosition(Math.min(100, Math.max(0, pct)));
  }, []);

  const onPointerDown = (e) => {
    draggingRef.current = true;
    e.currentTarget.setPointerCapture?.(e.pointerId);
    updateFromClientX(e.clientX);
  };
  const onPointerMove = (e) => {
    if (draggingRef.current) updateFromClientX(e.clientX);
  };
  const onPointerUp = () => { draggingRef.current = false; };

  return (
    <section className="bg-neutral-50 py-16 sm:py-20 md:py-24">
      <div className="mx-auto max-w-7xl px-4 md:px-8">
        <div className="mx-auto max-w-2xl text-center">
          <p className="text-sm font-bold uppercase tracking-widest text-primary">Before &amp; After</p>
          <h2 className="mt-3 text-3xl font-bold text-neutral-900 md:text-4xl">
            See the transformation
          </h2>
          <p className="mt-4 text-lg text-neutral-500">
            Drag the slider to compare a real room before and after visualization.
          </p>
        </div>

        <div
          ref={containerRef}
          className="relative mx-auto mt-12 aspect-[16/9] max-w-5xl cursor-ew-resize touch-none select-none overflow-hidden rounded-2xl shadow-xl shadow-neutral-900/10"
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
        >
          <img
            src={afterImg}
            alt="Room after visualization"
            className="absolute inset-0 h-full w-full object-cover"
            draggable={false}
          />
          <div
            className="absolute inset-0 overflow-hidden"
            style={{ clipPath: `inset(0 ${100 - position}% 0 0)` }}
          >
            <img
              src={beforeImg}
              alt="Room before visualization"
              className="absolute inset-0 h-full w-full object-cover"
              draggable={false}
            />
          </div>

          {/* Divider + handle */}
          <div
            className="absolute inset-y-0 z-10 w-0.5 bg-white shadow"
            style={{ left: `${position}%` }}
          >
            <div className="absolute left-1/2 top-1/2 flex h-11 w-11 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-white shadow-lg">
              <ChevronsLeftRight className="h-5 w-5 text-neutral-700" />
            </div>
          </div>

          <span className="absolute left-4 top-4 rounded-full bg-neutral-900/70 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-white backdrop-blur">
            Before
          </span>
          <span className="absolute right-4 top-4 rounded-full bg-primary/90 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-white backdrop-blur">
            After
          </span>
        </div>
      </div>
    </section>
  );
}
